import { Suspense, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import bRoutes from './routes/routeConfig';
import ModalFactory from './components/Modals/ModalFactory';
import LoadingSpinner from './components/Loaders/PageLoader/loadingSpinner'; // Import your new component
import Page from './pages/container/Page';
import ComingSoon from './pages/ComingSoon/ComingSoon';
import Home from './pages/Home/Home';
import {
  CART_STORAGE_KEY,
  useAppDispatch,
  useAppState,
} from './context/appState';
import { CartAPI } from './helpers/api/CartAPI';
import Error404, { ErrorPage } from './pages/Error404/Error404';
import useCatalog from './hooks/useCatalog';
import { ErrorBoundary } from 'react-error-boundary';

// ============================================================================
// COMMERCIAL PATTERN: Global Validation Lock
// Declared OUTSIDE the component so it survives React 18 Strict Mode remounts
// ============================================================================
let isValidatingCartStatus = false;
let hasValidatedCartStatus = false;

function App() {
  const dispatch = useAppDispatch();
  const { order } = useAppState();
  const { isReady, error } = useCatalog();

  const clearCart = useCallback(() => {
    dispatch({
      type: 'LOCAL_CART_CLEAR',
    });

    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear local storage:', error);
    }
  }, [dispatch]);

  useEffect(() => {
    const validateShopifyCart = async () => {
      const currentCartId = order?.id;

      // 1. Abort if there is no ID to check yet (e.g., still hydrating)
      if (!currentCartId) return;

      // 2. Abort if we are already checking, or have already checked this session
      if (isValidatingCartStatus || hasValidatedCartStatus) return;

      // 3. Lock the gate
      isValidatingCartStatus = true;

      try {
        const response = await CartAPI.checkCartStatus(currentCartId);

        if (response.data?.cart) {
          console.log(
            '[CART]: Abandoned checkout detected. Converting to local draft.',
          );
          dispatch({
            type: 'LOCAL_UNFINISHED_CART',
            id: null,
          });
        } else {
          console.log('[CART]: Checkout completed. Clearing local cart.');
          clearCart();
        }
      } catch (error) {
        clearCart();
        console.error('[CART]: Failed to validate cart status.', error);
      } finally {
        // 4. Unlock the fetcher and permanently mark as validated
        isValidatingCartStatus = false;
        hasValidatedCartStatus = true;
      }
    };

    validateShopifyCart();

    // We now safely track `order?.id`. The lock guarantees it won't spam!
  }, [order?.id, clearCart, dispatch]);

  return (
    <Router>
      <Routes>
        <Route
          index
          Component={
            import.meta.env.VITE_ENVIRONMENT == 'pre_release'
              ? ComingSoon
              : // : Home
                Home
          }
        />
        {bRoutes.map(({ name, path, component: Component }) => (
          <Route key={name} path={path} element={<Component />} />
        ))}
        <Route
          path="*"
          element={
            <Page>
              <Error404 />
            </Page>
          }
        />
      </Routes>

      <ModalFactory />
    </Router>
  );
}

export default App;
